import type { SubmitEventHandler } from 'react';
import type { DropdownOption } from '../../components/Dropdown/DropdownOption/DropdownOption';
import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type {
  AssignedUserRole,
  Space,
  User,
} from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import z from 'zod';

import {
  resetFormMeta,
  setFormSubmitErrors,
} from '../../form-hooks/lib/helpers';
import { getEnv } from '../../lib/env';
import { manageSubscription } from '../../lib/flotiq-client/api-helpers';
import { EMPTY_ARRAY } from '../../lib/globals';
import { getStripeURL, getTestProps } from '../../lib/helpers';
import { emailSchema, passwordSchema } from '../../lib/zodHelpers';

import Button from '../../components/Button/Button';
import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';
import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';

import useUsedApiKeyGuard from '../../hooks/usedApiKeyGuard/useUsedApiKeyGuard';
import useToken from '../../hooks/useToken';

import UserContext from '../../contexts/UserContext';

import { PolandFlagIcon, UnitedKingdomFlagIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const rolesOptionTranslationMap = {
  ROLE_HEADLESS_USER: (
    <Trans i18nKey={'Spaces.OrganizationRolesLabel.RoleHeadlessUser'} />
  ),
  ROLE_HEADLESS_ADMIN: (
    <Trans i18nKey={'Spaces.OrganizationRolesLabel.RoleHeadlessAdmin'} />
  ),
  ROLE_ADMIN: <Trans i18nKey={'Spaces.OrganizationRolesLabel.RoleAdmin'} />,
};

export type UserFormSubmitFn = (values: {
  lastName?: string;
  language?: string;
  spaces?: { id: string }[];
  roles?: string[];
  plainPassword?: string;
  email?: string;
  firstName?: string;
  paymentClientId?: string;
  username?: string;
  headlessRoles?: { id: string }[];
}) => Promise<Record<string, string> | null>;

interface UserFormProps {
  /**
   * On submit handler
   */
  onSubmit: UserFormSubmitFn;
  /**
   * User data
   */
  user?: User | null;
  /**
   * User mode between "add" or "edit"
   */
  mode?: string;
  /**
   * Is profile page
   */
  owner?: boolean;
  /**
   * Organization spaces
   */
  spacesData?: Space[];
  /**
   * All organization user roles
   */
  userRoles?: AssignedUserRole[];
  /**
   * Roles assigned to user
   */
  assignedRoles?: AssignedUserRole[];
  /**
   * Set form state
   */
  setFormState?: SetFormApiFn;
  /**
   * User form test id
   */
  testId?: string;
}

const UserForm = ({
  user,
  onSubmit,
  mode = 'edit',
  owner = false,
  spacesData,
  userRoles,
  assignedRoles = EMPTY_ARRAY,
  setFormState,
  testId = '',
}: UserFormProps) => {
  const { t } = useTranslation();
  const jwt = useToken();
  const { isRoleAdmin, isAdmin } = use(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const isInviteUserFlowWithResetPasswordEnabled = useMemo(
    () => getEnv('VITE_INVITE_USER_FLOW') === 'reset_password',
    [],
  );

  const validationSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(1, t('Form.FormErrorNotBlank')),
        lastName: z.string().min(1, t('Form.FormErrorNotBlank')),
        language: z.string().min(1, t('Form.FormErrorNotBlank')),
        spaces: z.array(
          z.object({
            id: z.string().min(1, t('Form.FormErrorNotBlank')),
            userRoles: z.array(z.string()),
          }),
        ),
        roles: z
          .array(z.string())
          .min(1, t('PropertyForm.Errors.OptionsLength')),
        ...(mode === 'add'
          ? {
              email: emailSchema(t),
              username: z.string().optional(),
              ...(!isInviteUserFlowWithResetPasswordEnabled
                ? {
                    plainPassword: passwordSchema(t),
                  }
                : { plainPassword: z.string().optional() }),
            }
          : {
              username: z.string().min(1, t('Form.FormErrorNotBlank')),
              email: z.string().optional(),
              plainPassword: z.string().optional(),
            }),
      }),
    [mode, isInviteUserFlowWithResetPasswordEnabled, t],
  );
  const organizationsDefaultSpace = useMemo(
    () =>
      (spacesData
        ?.filter((space) => space.defaultSpace)
        ?.map((space) => ({
          id: space.id,
          userRoles: [],
        })) || []) as { id: string; userRoles: string[] }[],
    [spacesData],
  );

  const initialValues = useMemo(
    () =>
      mode === 'add'
        ? {
            email: '',
            firstName: '',
            ...(!isInviteUserFlowWithResetPasswordEnabled && {
              plainPassword: '',
            }),
            lastName: '',
            language: 'en',
            spaces: organizationsDefaultSpace,
            roles: ['ROLE_HEADLESS_USER'],
          }
        : {
            firstName: user?.firstName,
            lastName: user?.lastName,
            username: user?.username,
            language: user?.language,
            spaces: (user?.spaces || []).map((space) => ({
              id: space.id,
              userRoles: assignedRoles
                .filter((role) => role.space?.id === space.id)
                .map(({ id }) => id),
            })),
            roles: user?.roles,
            ...(isRoleAdmin ? { paymentClientId: user?.paymentClientId } : {}),
          },
    [
      mode,
      isInviteUserFlowWithResetPasswordEnabled,
      organizationsDefaultSpace,
      user,
      isRoleAdmin,
      assignedRoles,
    ],
  );

  const initialSpaceIds = useMemo(
    () => initialValues?.spaces?.map(({ id }) => id),
    [initialValues],
  );

  const { usedApiKeyGuard } = useUsedApiKeyGuard(
    'userForm',
    user?.lastUsedApiKeys,
    mode,
  );

  const buttonSaveText =
    mode === 'add' ? t('Global.Invite') : t('Global.SaveChanges');

  const lngOptions = useMemo(
    () => [
      {
        value: 'en',
        searchString: t('UserForm.English'),
        label: (
          <div className="flex flex-row items-center">
            <UnitedKingdomFlagIcon className="w-5 h-5 mr-2" />
            {t('UserForm.English')}
          </div>
        ),
      },
      {
        value: 'pl',
        searchString: t('UserForm.Polish'),
        label: (
          <div className="flex flex-row items-center">
            <PolandFlagIcon className="w-5 h-5 mr-2" />
            {t('UserForm.Polish')}
          </div>
        ),
      },
    ],
    [t],
  );

  const spacesOptions = useMemo(
    () =>
      (isAdmin ? spacesData : user?.spaces)?.map(
        ({ id, name, planLimits }) => ({
          value: id,
          label: (
            <div className="flex items-center">
              {name}
              <div
                className={'bg-blue-300 rounded-sm px-2 ml-2 dark:text-default'}
              >
                {planLimits?.visible_name}
              </div>
            </div>
          ),
          searchString: 'spaces',
        }),
      ) || [],
    [isAdmin, spacesData, user?.spaces],
  );

  const rolesOptions = useMemo(
    () => [
      { value: 'ROLE_HEADLESS_USER', label: 'Organization User' },
      { value: 'ROLE_HEADLESS_ADMIN', label: 'Organization Admin' },
      ...(isRoleAdmin ? [{ value: 'ROLE_ADMIN', label: 'Flotiq Admin' }] : []),
    ],
    [isRoleAdmin],
  );

  const spaceLinks =
    user?.spaces && user?.spaces?.length > 0
      ? user.spaces.map(({ id, name }, idx) => (
          <LinkButton
            key={id}
            link={`/spaces/edit/${id}`}
            target="_blank"
            rel="noreferrer"
            buttonSize={'sm'}
            buttonColor={'borderless'}
            additionalClasses={'inline font-normal'}
          >
            {name} ↗{idx < user.spaces.length - 1 ? ', ' : ''}
          </LinkButton>
        ))
      : null;

  const manageSubscriptionClick = useCallback(
    () => manageSubscription(jwt, user?.id, t),
    [jwt, t, user?.id],
  );

  const userRolesBySpace = useMemo(
    () =>
      ((isAdmin ? spacesData : user?.spaces) || []).reduce(
        (roles, { id }) => {
          roles[id] =
            (isAdmin ? userRoles : assignedRoles)
              ?.filter(({ space }) => space?.id === id)
              ?.map((role) => ({ value: role.id, label: role.name })) || [];
          return roles;
        },
        {} as Record<string, DropdownOption[]>,
      ),
    [assignedRoles, isAdmin, spacesData, user, userRoles],
  );

  const stripeURL = getStripeURL(`/customers/${user?.paymentClientId || ''}`);

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      setIsLoading(true);

      const { spaces, ...otherValues } = value;
      const headlessRoles: { id: string }[] = [];
      spaces?.forEach(({ userRoles }) =>
        userRoles.forEach((id) => {
          headlessRoles.push({ id });
        }),
      );
      const spaceIds = spaces?.map(({ id }) => ({ id }));
      const spaceIdsArray = spaces?.map(({ id }) => id);

      const parsedDataDiff = ((isAdmin ? spacesData : user?.spaces) || [])
        .filter(
          ({ id }) =>
            initialSpaceIds.includes(id) && !spaceIdsArray.includes(id),
        )
        .map(({ id, name }) => ({ id, name }));

      const result = await usedApiKeyGuard(user?.email || '', parsedDataDiff);

      if (!result) {
        resetFormMeta(formApi);
        setIsLoading(false);
        return;
      }

      const errors = await onSubmit({
        ...otherValues,
        ...(isAdmin && {
          spaces: spaceIds,
          headlessRoles,
        }),
      });

      if (errors && Object.keys(errors).length > 0) {
        if (errors.global) {
          setGlobalError(errors.global);
        }
        setFormSubmitErrors(formApi, errors);
      } else {
        resetFormMeta(formApi);
      }

      setIsLoading(false);
    },
    validators: {
      onChange: validationSchema,
    },
  });

  const onCancel = useCallback(() => form.reset(), [form]);

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id="user-form"
        className="max-w-3xl"
        onSubmit={handleSubmit}
        noValidate={true}
        autoComplete="off"
      >
        <div className="flex flex-col gap-4 md:gap-6">
          <div className="text-xl md:text-2xl xl:text-3xl font-semibold dark:text-white">
            {t('UserForm.PersonalInformation')}
          </div>
          <div className="grid grid-cols-1 xs:grid-cols-2 items-center gap-4 md:gap-6 w-full">
            {mode === 'edit' && (
              <form.AppField name="username">
                {(field) => (
                  <field.Input
                    autoComplete="off"
                    placeholder={t('UserForm.Username')}
                    label={t('UserForm.Username')}
                    additionalInputClasses={
                      isLoading ? '' : 'bg-white! dark:bg-slate-900!'
                    }
                    disabled
                    required
                  />
                )}
              </form.AppField>
            )}

            {mode === 'add' && (
              <>
                <form.AppField name="email">
                  {(field) => (
                    <field.Input
                      autoComplete="off"
                      placeholder={'Email'}
                      label={'Email'}
                      additionalInputClasses={
                        isLoading ? '' : 'bg-white! dark:bg-slate-900!'
                      }
                      required
                      {...getTestProps(testId, 'email', 'testId')}
                    />
                  )}
                </form.AppField>

                {!isInviteUserFlowWithResetPasswordEnabled && (
                  <form.AppField name="plainPassword">
                    {(field) => (
                      <field.Input
                        autoComplete="new-password"
                        type="password"
                        placeholder={t('Global.Password')}
                        label={t('Global.Password')}
                        additionalInputClasses={
                          isLoading ? '' : 'bg-white! dark:bg-slate-900!'
                        }
                        showPasswordInfo
                        required
                        {...getTestProps(testId, 'plain-password', 'testId')}
                      />
                    )}
                  </form.AppField>
                )}
              </>
            )}

            <form.AppField name="firstName">
              {(field) => (
                <field.Input
                  placeholder={t('Global.FirstName')}
                  label={t('Global.FirstName')}
                  disabled={isLoading}
                  additionalClasses="xs:row-start-2"
                  required
                  {...getTestProps(testId, 'first-name', 'testId')}
                />
              )}
            </form.AppField>

            <form.AppField name="lastName">
              {(field) => (
                <field.Input
                  placeholder={t('Global.LastName')}
                  label={t('Global.LastName')}
                  disabled={isLoading}
                  additionalClasses="xs:row-start-2"
                  required
                  {...getTestProps(testId, 'last-name', 'testId')}
                />
              )}
            </form.AppField>

            <form.AppField name="language">
              {(field) => (
                <field.Dropdown
                  label={t('UserForm.Language')}
                  options={lngOptions}
                  disabled={isLoading}
                  additionalClasses="xs:row-start-3"
                  hideSearch
                  required
                />
              )}
            </form.AppField>
          </div>

          <div className="flex flex-col">
            <Heading level={4} additionalClasses="dark:text-white pb-0">
              {t('Spaces.OrganizationRoles')}
            </Heading>
          </div>

          <div>
            <form.AppField name="roles">
              {(field) => (
                <field.Dropdown
                  label={t('UserForm.OrganizationRole')}
                  options={rolesOptions}
                  disabled={isLoading || !isAdmin}
                  additionalClasses="max-w-sm pr-2"
                  hideSearch
                  multiple
                  required
                  {...getTestProps(testId, 'roles', 'testId')}
                />
              )}
            </form.AppField>
            <div className="flex flex-col pt-2">
              <form.Subscribe selector={(state) => state.values.roles}>
                {(roles) => (
                  <>
                    {roles?.map((role) => {
                      return (
                        <label
                          className="py-1 dark:text-white"
                          key={`role-${role}-label`}
                        >
                          {role in rolesOptionTranslationMap
                            ? rolesOptionTranslationMap[
                                role as keyof typeof rolesOptionTranslationMap
                              ]
                            : role}
                        </label>
                      );
                    })}
                  </>
                )}
              </form.Subscribe>
            </div>
          </div>

          <div className="flex flex-col">
            <Heading level={4} additionalClasses="dark:text-white pb-0">
              {t('Spaces.Spaces')}
            </Heading>
          </div>

          <div className="w-full">
            <form.AppField name={'spaces'} mode="array">
              {(field) => (
                <field.SpaceSelectorField
                  spaces={spacesOptions}
                  userRolesBySpace={userRolesBySpace}
                  disabled={!isAdmin || isLoading}
                  testId={testId}
                />
              )}
            </form.AppField>

            {isRoleAdmin && spaceLinks && (
              <div className="inline-flex flex-wrap gap-1 mt-2 dark:text-white text-sm max-w-full">
                <Trans i18nKey="UserForm.SpaceLinks">
                  Go to space:
                  <span className="flex flex-wrap gap-1">{spaceLinks}</span>
                </Trans>
              </div>
            )}
          </div>

          {isRoleAdmin && !owner && mode === 'edit' && stripeURL && (
            <>
              <Heading level={4} additionalClasses="mt-0 dark:text-white">
                Admin
              </Heading>

              <div className="grid grid-cols-1 xs:grid-cols-2 items-center w-full">
                <form.AppField name="paymentClientId">
                  {(field) => (
                    <field.Input
                      label={t('Users.PaymentClientId')}
                      disabled={isLoading}
                      additionalClasses="pr-2"
                      helpText={
                        <div className="mt-1">
                          <Trans i18nKey="UserForm.PaymentLinks">
                            See
                            <Button
                              buttonSize="sm"
                              buttonColor={'borderless'}
                              additionalClasses={'inline font-normal'}
                              onClick={manageSubscriptionClick}
                            >
                              Stripe manage panel
                            </Button>
                            <LinkButton
                              link={stripeURL}
                              target="_blank"
                              rel="noreferrer"
                              buttonSize={'sm'}
                              buttonColor={'borderless'}
                              additionalClasses={'inline font-normal'}
                            >
                              Stripe customer panel
                            </LinkButton>
                          </Trans>
                        </div>
                      }
                    />
                  )}
                </form.AppField>
              </div>
            </>
          )}

          <div>
            <div className="flex flex-row items-center gap-2 md:gap-6">
              <SaveButton
                form="user-form"
                isSaving={isLoading}
                label={buttonSaveText}
                {...getTestProps(testId, 'submit', 'testId')}
              />
              <form.Subscribe selector={({ isDirty }) => isDirty}>
                {(isDirty) =>
                  isDirty && (
                    <CancelButton
                      onClick={onCancel}
                      disabled={isLoading}
                      {...getTestProps(testId, 'cancel', 'testId')}
                    />
                  )
                }
              </form.Subscribe>
            </div>
            {globalError && (
              <div className="text-red mt-2 inline-flex">
                <div className="block text-left">{globalError}</div>
              </div>
            )}
          </div>
        </div>
        <form.DirtyHandler />
        <form.FormStateHandler setFormState={setFormState} />
      </form>
    </form.AppForm>
  );
};

export default UserForm;
